/**
 * ChainNode.tsx - Custom React Flow node for consolidated inference chain viewer
 *
 * Displays a single concept node in the BSC-columnar DAG with:
 * - BSC perspective color coding
 * - chainRefs badge (how many original chains reference this node)
 * - Anchor indicator
 * - Gap node styling (dashed border, warning icon)
 * - Hover tooltip with source chain info
 */

import { memo } from 'react'
import { Handle, Position, NodeProps } from 'reactflow'
import type { BSCPerspective } from '@models/oracle'
import './ChainNode.css'

// BSC perspective icons and colors
const BSC_CONFIG: Record<string, { icon: string; color: string; label: string }> = {
  'Learning_Growth': { icon: '📚', color: '#3b82f6', label: 'Learning & Growth' },
  'Internal_Process': { icon: '⚙️', color: '#10b981', label: 'Internal Process' },
  'Customer': { icon: '👤', color: '#f97316', label: 'Customer' },
  'Financial': { icon: '💰', color: '#ef4444', label: 'Financial' },
  'unknown': { icon: '❓', color: '#6b7280', label: 'Unknown' },
}

export interface ChainNodeMetric {
  id: string
  name: string
  formula?: string
  unit?: string
}

export interface ChainNodeKSA {
  id: string
  name: string
  summary?: string
}

export interface ChainNodeRefDoc {
  id: string
  name: string
  description?: string
}

export interface ChainNodeData {
  concept: string
  nodeId: string | null
  perspective: BSCPerspective | 'unknown'
  isAnchor: boolean
  isGap: boolean
  confidence: number
  chainRefs?: number
  totalChains?: number
  sourceChainIndices?: number[]
  direction?: string
  chainIndex?: number
  isSelected?: boolean
  isDimmed?: boolean
  isHighlighted?: boolean
  metrics?: ChainNodeMetric[]  // KPI Grounding: Associated metrics
  ksas?: ChainNodeKSA[]  // Competency Grounding: Associated KSAs
  ref_docs?: ChainNodeRefDoc[]  // Evidence Grounding: Associated references
}

function ChainNode({ data }: NodeProps<ChainNodeData>) {
  const {
    concept,
    nodeId,
    perspective,
    isAnchor,
    isGap,
    confidence,
    chainRefs,
    totalChains,
    direction,
    isSelected,
    isDimmed,
    isHighlighted,
  } = data

  const config = BSC_CONFIG[perspective] || BSC_CONFIG.unknown
  const displayName = concept
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())

  // Qualitative confidence label (numeric percentages were engineering-detail
  // noise; advisors only need a coarse signal here — the precise score lives
  // in the response-level Confidence breakdown panel).
  const getConfidenceClass = () => {
    if (isGap) return 'gap'
    if (confidence >= 0.85) return 'high'
    if (confidence >= 0.70) return 'medium'
    if (confidence >= 0.50) return 'low'
    return 'insufficient'
  }

  const confidenceLabel: Record<string, string> = {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    insufficient: 'Insufficient',
  }

  const classNames = [
    'chain-node',
    isAnchor ? 'anchor' : '',
    isGap ? 'gap' : '',
    isSelected ? 'selected' : '',
    isDimmed ? 'dimmed' : '',
    isHighlighted ? 'highlighted' : '',
  ].filter(Boolean).join(' ')

  // Title surfaces the ontology node id for power users without polluting
  // the visible label with snake_case noise.
  const nodeTitle = !isGap && nodeId ? `Ontology node: ${nodeId}` : undefined

  return (
    <div
      className={classNames}
      style={{
        borderColor: isGap ? '#f59e0b' : config.color,
      }}
      title={nodeTitle}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: config.color }}
      />

      {/* chainRefs badge */}
      {chainRefs != null && chainRefs > 1 && (
        <div
          className="chain-ref-badge"
          title={`Referenced by ${chainRefs} of ${totalChains || '?'} chains`}
        >
          ×{chainRefs}
        </div>
      )}

      {/* Anchor indicator */}
      {isAnchor && (
        <div className="anchor-badge" title="Anchor concept (query term)">
          ⚓
        </div>
      )}

      {/* Node content */}
      <div className="node-header">
        <span className="perspective-icon">{isGap ? '⚠️' : config.icon}</span>
        <span className="concept-name">{displayName}</span>
        {isAnchor && direction && (
          <span className="direction-badge">
            {direction === 'upstream' ? '↑' : direction === 'downstream' ? '↓' : '↔'}
          </span>
        )}
      </div>

      <div className="node-divider" style={{ borderColor: config.color + '40' }} />

      {!isGap && (
        <div
          className={`confidence-swatch ${getConfidenceClass()}`}
          title={`${confidenceLabel[getConfidenceClass()] ?? 'Unknown'} confidence`}
          aria-label={`${confidenceLabel[getConfidenceClass()] ?? 'Unknown'} confidence`}
        >
          <span className="confidence-swatch-dot" />
          <span className="confidence-swatch-label">{confidenceLabel[getConfidenceClass()] ?? '—'}</span>
        </div>
      )}

      {isGap && (
        <div className="gap-indicator">
          <span className="gap-label">Ontology gap</span>
        </div>
      )}

      {/* KPI Grounding: Associated metrics chip */}
      {data.metrics && data.metrics.length > 0 && (
        <div
          className="metrics-indicator chip-prominent"
          title={data.metrics.map(m => `${m.name}${m.formula ? ` = ${m.formula}` : ''}${m.unit ? ` [${m.unit}]` : ''}`).join('\n')}
        >
          <span className="metrics-icon">📊</span>
          <span className="metrics-text">
            {data.metrics.length === 1
              ? data.metrics[0].name
              : `${data.metrics.length} KPIs`}
          </span>
        </div>
      )}

      {/* Competency Grounding: Associated KSA chip */}
      {data.ksas && data.ksas.length > 0 && (
        <div
          className="ksas-indicator chip-prominent"
          title={data.ksas.map(k => `${k.name}${k.summary ? `: ${k.summary}` : ''}`).join('\n')}
        >
          <span className="ksas-icon">🎓</span>
          <span className="ksas-text">
            {data.ksas.length === 1
              ? data.ksas[0].name.replace(/\s*KSA\s*Document$/i, '')
              : `${data.ksas.length} Competencies`}
          </span>
        </div>
      )}

      {/* Evidence Grounding: Associated Ref_Document chip */}
      {data.ref_docs && data.ref_docs.length > 0 && (
        <div
          className="ref-docs-indicator chip-prominent"
          title={data.ref_docs.map(r => `${r.name}${r.description ? `: ${r.description}` : ''}`).join('\n')}
        >
          <span className="ref-docs-icon">📄</span>
          <span className="ref-docs-text">
            {data.ref_docs.length === 1
              ? data.ref_docs[0].name.replace(/_/g, ' ')
              : `${data.ref_docs.length} References`}
          </span>
        </div>
      )}

      {/* Perspective tag */}
      <div
        className="perspective-tag"
        style={{ background: config.color + '20', color: config.color }}
      >
        {config.label}
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: config.color }}
      />
    </div>
  )
}

export default memo(ChainNode)
